import { Injectable } from '@angular/core';
import { isArray } from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { VariableService } from '@services/variable/variable.service';

@Injectable({ providedIn: 'root' })
export class ErrorFormService {
    errors: Observable<any>;
    errorsSubject: BehaviorSubject<any>;

    private initSidebarErrors = new Subject<void>();
    initSidebarErrors$ = this.initSidebarErrors.asObservable();

    private updateOnFieldChange = new Subject<string>();
    updateOnFieldChange$ = this.updateOnFieldChange.asObservable();

    constructor(private toastr: ToastrService, private translate: TranslateService, private variableService: VariableService) {
        this.errorsSubject = new BehaviorSubject<any>([]);
        this.errors = this.errorsSubject.asObservable();
    }

    initErrors(error) {
        let message = '';
        if (isArray(error)) {
            message = error.filter(e => e.error).reduce((acc, cur) => `${acc} - ${this.getErrorMessage(cur)}</br>`, '');
        }
        this.toastr.error(message, this.translate.instant('errors.patient.title'), {
            closeButton: true,
            disableTimeOut: true,
            enableHtml: true,
        });
        this.errorsSubject.next(isArray(error) ? error : []);
    }

    getErrorMessage(error) {
        const service = error.field?.split('.')?.[0];

        if (error.error === 'REQUIRED') {
            return this.translate.instant('errors.patient.REQUIRED', {
                variable: this.variableService.variables.find(v => v.key === error.key)?.name[this.translate.currentLang || 'fr'] || error.key,
            });
        }

        if (error.error === 'INVALID') {
            return this.translate.instant('errors.patient.INVALID', {
                variable: this.variableService.variables.find(v => v.key === error.key)?.name[this.translate.currentLang || 'fr'] || error.key,
            });
        }

        if (service && error.error === 'errors.company.MISSING_SERVICENAME') {
            return this.translate.instant('errors.company.MISSING_SERVICENAME_SERVICE', {
                service: this.translate.instant('journey.' + service),
            });
        }

        if (service && error.error === 'errors.company.MISSING_PHONE') {
            return this.translate.instant('errors.company.MISSING_PHONE_SERVICE', {
                service: this.translate.instant('journey.' + service),
            });
        }

        if (service && error.error === 'errors.company.DISPLAYED_PHONE_MOBILE') {
            return this.translate.instant('errors.company.DISPLAYED_PHONE_MOBILE_SERVICE', {
                service: this.translate.instant('journey.' + service),
            });
        }

        return this.translate.instant(error.error);
    }

    removeError(error) {
        this.errorsSubject.next(this.errorsSubject.getValue().filter(({ field }) => field !== error));
    }

    removeErrorKey(error) {
        this.errorsSubject.next(this.errorsSubject.getValue().filter(({ key }) => key !== error));
    }

    resetErrors() {
        this.errorsSubject.next([]);
    }

    removeTreatment(index) {
        const errors = this.errorsSubject.getValue().filter(({ field }) => !field?.startsWith(`treatmentImmunosuppressant.${index}`));

        errors
            .filter(error => error.field?.startsWith('treatmentImmunosuppressant'))
            .forEach(error => {
                const i = error.field['treatmentImmunosuppressant.'.length];

                if (i > index) {
                    error.field = error.field.replace(i, +i - 1);
                }
            });

        this.errorsSubject.next(errors);
    }

    initSidebarError() {
        this.initSidebarErrors.next();
    }

    updateSidebarOnFieldChanged(field: string) {
        this.updateOnFieldChange.next(field);
    }

    hasFeedbackError(status, feedback: string): boolean {
        const inputError = this.errorsSubject.getValue()?.some(({ error }) => error?.endsWith(feedback));
        status.error ||= inputError;
        return inputError;
    }

    hasError(status, error: string): boolean {
        const inputError = this.errorsSubject.getValue()?.some(({ field }) => field === error);
        status.error ||= inputError;
        return inputError;
    }
}
